import type { Numbers } from "./engine";

/**
 * The whirl ribbon from bot-avatars (ported from Alex's Ask Opus orb
 * playground): a tilted ring round the orb while it spins, drawn in 2D as
 * tapering bands over 1.55π, half behind the body and half in front. Drawn
 * only while the pose's `wh` (whirl strength) is up; `wa` is its angle.
 */

const RIB = {
  segs: 90,
  span: Math.PI * 1.55,
  tiltRot: -0.28,
  rx: 1.55,
  flat: 0.4,
};
type Rgb = readonly [number, number, number];
export type RibbonPalette = Record<"halo" | "dark" | "base" | "light", Rgb>;
/** The Ask Opus blue and gold. */
export const RIBBON_BRAND: RibbonPalette = {
  halo: [255, 226, 118],
  dark: [68, 89, 217],
  base: [133, 156, 253],
  light: [173, 189, 255],
};
/** A graphite agent at rest. */
export const RIBBON_GRAPHITE: RibbonPalette = {
  halo: [150, 150, 160],
  dark: [20, 20, 24],
  base: [45, 45, 52],
  light: [120, 120, 130],
};
const rgba = ([r, g, b]: Rgb, a: number) =>
  `rgba(${r},${g},${b},${Math.max(0, Math.min(1, a)).toFixed(3)})`;
// The bots' light, 265°.
const LIGHT = Math.atan2(0.087, -0.996) + 0.28;

type Sample = {
  F: number;
  front: boolean;
  w: number;
  x: number;
  y: number;
  nx: number;
  ny: number;
  al: number;
  L: number;
};

/** The body on screen: centre, radius and squash, in canvas px. */
export type RibbonBody = { cx: number; cy: number; R: number; sq: number };

/** Where the ribbon goes for this pose, or null when it isn't showing. */
export const ribbonBody = (
  P: Numbers,
  x: number,
  y: number,
  size: number,
): RibbonBody | null =>
  (P.wh ?? 0) > 0.01
    ? {
        // Fitted: the head fills 0.377 of the box; one unit is 0.419 of it.
        R: 0.377 * size * P.hs,
        cx: x + size / 2,
        cy: y + size / 2 - P.oy * 0.419 * size,
        sq: P.sq,
      }
    : null;

/** Draw the back half (`front` false, outside the body only) or the front half. */
export const drawRibbon = (
  ctx: CanvasRenderingContext2D,
  { cx, cy, R, sq }: RibbonBody,
  P: Numbers,
  front: boolean,
  RGB: RibbonPalette = RIBBON_BRAND,
  /** How far in the body's visible edge sits, as a fraction of R (a
   * wobbly edge pulls in); the back half is clipped there, not at R. */
  edgeInset = 0,
): void => {
  const n = Math.min(1, P.wh ?? 0);
  if (n <= 0.01) return;
  const k = R / 35;
  const rx = R * RIB.rx;
  const ry = rx * RIB.flat * (front ? 1.14 : 0.86);
  const A = -(P.wa ?? 0);
  ctx.save();
  if (!front) {
    // The back half only shows outside the orb.
    ctx.beginPath();
    ctx.rect(0, 0, ctx.canvas.width, ctx.canvas.height);
    const clipR = R * (1.02 - edgeInset);
    ctx.ellipse(cx, cy, clipR, clipR * sq, 0, 0, Math.PI * 2);
    ctx.clip("evenodd");
  }
  ctx.translate(cx, cy + 5 * k);
  ctx.rotate(RIB.tiltRot);
  // Samples along the ring: how far down the tail (F), the swell, the light.
  const pts: Sample[] = [];
  for (let g = 0; g <= RIB.segs; g++) {
    const F = g / RIB.segs;
    const a = A + F * RIB.span;
    const C = 0.6 + 0.4 * Math.sin(a);
    const I = Math.pow(1 - F, 1.3);
    const wob = 1 + 0.18 * Math.sin(F * 9 + 1.2);
    const nx = ry * Math.cos(a);
    const ny = rx * Math.sin(a);
    const nl = Math.hypot(nx, ny) || 1;
    pts.push({
      F,
      front: Math.sin(a) > 0,
      w: (2 + 8 * I) * C * wob * k * 1.5 * Math.sqrt(Math.min(1, F / 0.05)),
      x: rx * Math.cos(a),
      y: ry * Math.sin(a),
      nx: nx / nl,
      ny: ny / nl,
      al: (0.3 + 0.7 * I) * C,
      L: 0.5 + 0.5 * Math.cos(a - LIGHT),
    });
  }
  // Contiguous runs on this side of the orb.
  const runs: Sample[][] = [];
  let cur: Sample[] | null = null;
  for (const q of pts) {
    if (q.front === front) (cur ??= []).push(q);
    else if (cur) {
      runs.push(cur);
      cur = null;
    }
  }
  if (cur) runs.push(cur);
  const band = (
    run: Sample[],
    scale: number,
    dy: number,
    fill: (a: number) => string,
    alpha: number,
  ) => {
    if (run.length < 2) return;
    ctx.beginPath();
    run.forEach((q, i) => {
      const h = (q.w * scale) / 2;
      const px = q.x + q.nx * h;
      const py = q.y + q.ny * h + dy * q.w;
      if (i) ctx.lineTo(px, py);
      else ctx.moveTo(px, py);
    });
    for (let i = run.length - 1; i >= 0; i--) {
      const q = run[i];
      const h = (q.w * scale) / 2;
      ctx.lineTo(q.x - q.nx * h, q.y - q.ny * h + dy * q.w);
    }
    ctx.closePath();
    // Fade along the tail with a gradient from its head to its end.
    const h0 = run[0];
    const h1 = run[run.length - 1];
    const gr = ctx.createLinearGradient(h0.x, h0.y, h1.x, h1.y);
    gr.addColorStop(0, fill(alpha * h0.al));
    gr.addColorStop(1, fill(alpha * h1.al));
    ctx.fillStyle = gr;
    ctx.fill();
  };
  for (const run of runs) {
    const Lm = run.reduce((s, q) => s + q.L, 0) / run.length;
    if (front)
      band(run, 1.4, 0.35, (a) => `rgba(0,0,0,${a.toFixed(3)})`, 0.1 * n);
    band(run, 2.4, 0, (a) => rgba(RGB.halo, a), 0.3 * n);
    band(run, 0.8, 0.32, (a) => rgba(RGB.dark, a), 0.45 * n);
    band(run, 1.0, 0, (a) => rgba(RGB.base, a), 0.72 * n);
    band(
      run,
      0.62,
      -0.16,
      (a) => rgba(RGB.light, a),
      0.78 * n * (0.4 + 0.6 * Lm),
    );
    band(
      run,
      0.24,
      -0.3,
      (a) => `rgba(255,255,255,${a.toFixed(3)})`,
      0.9 * n * (0.15 + 0.85 * Lm * Lm),
    );
  }
  ctx.restore();
};
